import Element from 'ui/Element';
import HiddenCollapsedMixin from './mixins/HiddenCollapsedMixin';
import ClickableMixin from './mixins/ClickableMixin';
import LinkableMixin from './mixins/LinkableMixin';
import AnimatableMixin from './mixins/AnimatableMixin';
import mixin from 'privates/utils/mixin';
import * as members from 'privates/privateMembers';
import vectorImageUtils from 'privates/utils/vectorImageUtils';

/**
 * @class VectorImage
 * @summary Vector images are digital illustrations based on geometric shapes.
 *  They maintain their high quality when resized.
 * @mixes $w.Element
 * @mixes $w.HiddenCollapsedMixin
 * @mixes $w.LinkableMixin
 * @mixes $w.ClickableMixin
 * @memberof $w
 * @viewername wysiwyg.viewer.components.svgshape.SvgShape
 * @viewername wysiwyg.viewer.components.VectorImage
 * @AppController wysiwyg.viewer.components.svgshape.SvgShape
 * @AppController wysiwyg.viewer.components.VectorImage
 * @tagname vectorImage
 */
class VectorImage extends Element {

    /**
     * @member src
     * @label property
     * @syntax
     * get src(): string
     * set src(value: string): void
     * @summary Sets or gets the file location of the vector image.
     * @description
     *  Setting the `src` property changes the displayed vector image to
     *  the vector image found at the new `src` value.
     *
     *  Getting the `src` property returns the location of the
     *  current vector image file.
     *
     *  The vector image file is an image file from the <a href="https://support.wix.com/en/article/about-the-media-manager" target="_blank">Media Manager</a>,
     *  an external SVG image from any web location, or a literal SVG XML string.
     *
     *  The formats supported are:
     *
     *  + Vector images from the Media Manager: `wix:vector://v1/<vector_uri>/<filename>`
     *  + Vector images from the web: `http(s)://<vector image url>`
     *  + Vector XML string: `<svg>...</svg>`
     * @snippet [VectorImage-src_get.es6=Get the vector image's location]
     * @snippet [VectorImage-src_set_wix.es6=Set the vector image to be a vector image from the Media Manager]
     * @snippet [VectorImage-src_set_web.es6=Set the vector image to be a vector image from the web]
     * @snippet [VectorImage-src_set_xml.es6=Set the vector image to be a vector image from an XML string]
     * @type {external:String}
     * @default An empty string
     * @memberof $w.VectorImage
     * @instance
     * @attr {external:String}
     */
    get src() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compData = RMI.getData(compId) || {};

        if(compData.hasOwnProperty('mediaSrc')) {
            return compData.mediaSrc.value;
        }

        return vectorImageUtils.buildFullUrlFromSvgId(compData.svgId, compData.title);
    }

  /**
   * @member src
   * @memberof $w.VectorImage
   * @param {external:String} value
   */
    set src(value) {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);

        RMI.setData(compId, {mediaSrc: vectorImageUtils.convertToMediaSrcAndReportIfError(value, 'src', 'src')});
    }

    toJSON() {
        const {id, type, global, rendered, src} = this;
        return {id, type, global, rendered, src};
    }
}

mixin(VectorImage.prototype, HiddenCollapsedMixin.prototype);
mixin(VectorImage.prototype, LinkableMixin.prototype);
mixin(VectorImage.prototype, ClickableMixin.prototype);
mixin(VectorImage.prototype, AnimatableMixin.prototype);

export default VectorImage;



// WEBPACK FOOTER //
// ./js/modules/ui/VectorImage.es6